import React, { FC, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { releases } from "../../../apis"
import { lang } from "../../../constants/languages"
import { RootState } from "../../../store"
import { Artist, ArtistSearch, SelectOption } from "../../../types"
import { mapListToCamelCase, mapToCamelCase, removeDuplicates } from "../../../utility"
import { SearchSelect } from "../../ui/form/select/SearchSelect"

// The "+ Add" row carries the typed name behind this prefix, so a name that is also a search result stays
// a separate row.
const ADD_NEW_PREFIX = "add_new_"
const AVATAR = "/reports_icons/avatar.svg"
const SEARCH_DELAY_MS = 400

// The account's own artists and contributors, read once for every picker on the page.
let knownArtists: Promise<SelectOption[]> | null = null

/** {name: [spotify id]} the pickers fill in as names are picked from the search, sent with the save. */
export type SpotifyMap = { [name: string]: string[] }

const toOption = (artist: Artist, spotifyMap: SpotifyMap): SelectOption => {
  if (artist.spotifyId) {
    spotifyMap[artist.name] = spotifyMap[artist.name] || []
    if (!spotifyMap[artist.name].includes(artist.spotifyId)) spotifyMap[artist.name].push(artist.spotifyId)
  }
  return { value: artist.name, label: artist.name, spotifyId: artist.spotifyId, cover: artist.cover || AVATAR }
}

/**
 * The artist picker of the release registry, on the release plan: names searched on Spotify and among
 * the account's artists, or typed in as new. Several names per field, as idol keeps them.
 */
const ReleasePlanArtistPicker: FC<{
  value: string[]
  onChange: (names: string[]) => void
  spotifyMap: SpotifyMap
  placeholder: string
}> = ({ value, onChange, spotifyMap, placeholder }) => {
  const user = useSelector((state: RootState) => state.auth.user)
  const [known, setKnown] = useState<SelectOption[]>([])
  const [found, setFound] = useState<SelectOption[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!user?.id) return
    if (!knownArtists) {
      knownArtists = releases(`/release/get_artists_and_contributors/?user_id=${user.id}`, "get")
        .then((response) =>
          removeDuplicates(
            mapListToCamelCase(response?.data?.results || []).map((artist: Artist) => toOption(artist, spotifyMap))
          )
        )
        .catch(() => {
          knownArtists = null
          return []
        })
    }
    knownArtists.then(setKnown)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [user?.id])

  const search = (text: string) => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      if (!text) return setFound([])
      const addNew = { value: `${ADD_NEW_PREFIX}${text}`, label: lang.catalog.asset_registry.add_new, cover: AVATAR }
      releases(`/artist/search/?value=${encodeURIComponent(text)}&sp_enabled=1`, "get")
        .then((response) => {
          const result: ArtistSearch = mapToCamelCase(response?.data?.result || {})
          setFound([
            addNew,
            ...(result.spotify || []).map((artist) => toOption(artist, spotifyMap)),
            ...removeDuplicates((result.kahuna || []).map((artist) => toOption(artist, spotifyMap)))
          ])
        })
        .catch(() => setFound([addNew]))
    }, SEARCH_DELAY_MS)
  }

  // The picked names are always among the options, the field shows each by its option's label.
  const picked: SelectOption[] = value.map((name) => ({ value: name, label: name, cover: AVATAR }))
  const options = found.length ? [...found, ...picked] : removeDuplicates([...picked, ...known])

  return (
    <SearchSelect
      className="border-0"
      placeholder={placeholder}
      containerStyle={{ border: "1px solid #E6E6E6", borderRadius: 10, boxShadow: "none", minHeight: 40 }}
      inputTextClass="text-[14px]"
      multiple
      // The search text goes with the pick: left in the field, it no longer matched the list under it.
      clearTextOnSelect
      options={options}
      value={value}
      onTextChange={(event) => search(event.target.value)}
      //@ts-ignore
      onChange={(names: string[]) => {
        const cleaned = names.map((name) => (name.startsWith(ADD_NEW_PREFIX) ? name.replace(ADD_NEW_PREFIX, "") : name))
        onChange(cleaned.filter((name, index) => name && cleaned.indexOf(name) === index))
        setFound([])
      }}
    />
  )
}

export default ReleasePlanArtistPicker
